import { create } from "zustand";
import type { CartPayloadGetCartItemsResponse, DTOCartItemView, DTOCartSummary } from "../../../generated/api-client";
import type { LocaleCode } from "@/i18n/locales";
import { cartApi } from "@/services/cart/cart-api";
import { useAuthStore } from "./auth-store";

interface CartState {
    quantities: Record<string, number>;
    cart: Record<string, number>;
    cartItems: Record<string, DTOCartItemView>;
    summary: DTOCartSummary | null;
    pendingProductIds: Record<string, boolean>;
    pendingSessionVersions: Record<string, number>;
    loadedLocale: LocaleCode | null;
    error: string | null;
    setQuantity: (id: string, quantity: number) => void;
    setCartQuantity: (id: string, quantity: number, locale: LocaleCode) => Promise<boolean>;
    addToCart: (id: string, locale: LocaleCode) => Promise<boolean>;
    removeFromCart: (id: string, locale: LocaleCode) => Promise<boolean>;
    loadCart: (locale: LocaleCode) => Promise<void>;
    clearCart: () => void;
}

function cartState(data: CartPayloadGetCartItemsResponse) {
    return {
        cart: Object.fromEntries(data.cartItems.map(item => [item.productId, item.quantity])),
        cartItems: Object.fromEntries(data.cartItems.map(item => [item.productId, item])),
        summary: data.summary
    };
}

function updateWithCartTransition(update: () => void): void {
    const transitionDocument: Document & { startViewTransition?: (callback: () => void) => unknown } = document;
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (!transitionDocument.startViewTransition || reduceMotion) {
        update();
        return;
    }
    transitionDocument.startViewTransition(update);
}

let cartMutationQueue: Promise<void> = Promise.resolve();
let cartOperationVersion = 0;

export const useCartStore = create<CartState>((set, get) => {
    const changeServerQuantity = async (id: string, targetQuantity: number, locale: LocaleCode): Promise<boolean> => {
        const state = get();
        if (state.pendingProductIds[id]) return false;
        const requestSessionVersion = useAuthStore.getState().sessionVersion;
        const clearPendingIfOwned = () => set(current => {
            if (current.pendingSessionVersions[id] !== requestSessionVersion) return current;
            const pendingProductIds = { ...current.pendingProductIds };
            const pendingSessionVersions = { ...current.pendingSessionVersions };
            delete pendingProductIds[id];
            delete pendingSessionVersions[id];
            return { pendingProductIds, pendingSessionVersions };
        });

        set(current => ({
            pendingProductIds: { ...current.pendingProductIds, [id]: true },
            pendingSessionVersions: { ...current.pendingSessionVersions, [id]: requestSessionVersion },
            error: null
        }));
        const mutation = cartMutationQueue.then(async () => {
            const currentAuth = useAuthStore.getState();
            if (currentAuth.status !== "authenticated" || currentAuth.sessionVersion !== requestSessionVersion) {
                clearPendingIfOwned();
                return false;
            }
            const currentQuantity = get().cart[id] ?? 0;
            const quantityDelta = targetQuantity - currentQuantity;
            if (quantityDelta === 0) {
                clearPendingIfOwned();
                return true;
            }

            try {
                const operationVersion = ++cartOperationVersion;
                const data = await cartApi.changeQuantity(id, quantityDelta, locale);
                const completedAuth = useAuthStore.getState();
                if (completedAuth.status !== "authenticated" || completedAuth.sessionVersion !== requestSessionVersion || operationVersion !== cartOperationVersion) {
                    clearPendingIfOwned();
                    return false;
                }
                updateWithCartTransition(() => {
                    set(current => ({
                        ...cartState(data),
                        loadedLocale: locale,
                        pendingProductIds: { ...current.pendingProductIds, [id]: false },
                        pendingSessionVersions: Object.fromEntries(Object.entries(current.pendingSessionVersions).filter(([productId]) => productId !== id))
                    }));
                });
                return true;
            } catch (error) {
                set(current => current.pendingSessionVersions[id] === requestSessionVersion ? {
                    pendingProductIds: { ...current.pendingProductIds, [id]: false },
                    pendingSessionVersions: Object.fromEntries(Object.entries(current.pendingSessionVersions).filter(([productId]) => productId !== id)),
                    error: error instanceof Error ? error.message : "Cart update failed."
                } : current);
                return false;
            }
        });
        cartMutationQueue = mutation.then(() => undefined);
        return mutation;
    };

    return {
        quantities: {},
        cart: {},
        cartItems: {},
        summary: null,
        pendingProductIds: {},
        pendingSessionVersions: {},
        loadedLocale: null,
        error: null,
        setQuantity: (id, quantity) => set(state => ({ quantities: { ...state.quantities, [id]: Math.max(1, quantity) } })),
        setCartQuantity: async (id, quantity, locale) => {
            if (useAuthStore.getState().status === "authenticated") {
                return changeServerQuantity(id, Math.max(0, quantity), locale);
            }
            updateWithCartTransition(() => {
                set(state => {
                    const cart = { ...state.cart };
                    if (quantity <= 0) delete cart[id];
                    else cart[id] = quantity;
                    return { cart };
                });
            });
            return true;
        },
        addToCart: async (id, locale) => {
            const quantity = get().quantities[id] ?? 1;
            if (useAuthStore.getState().status === "authenticated") {
                return changeServerQuantity(id, (get().cart[id] ?? 0) + quantity, locale);
            }
            updateWithCartTransition(() => {
                set(state => ({ cart: { ...state.cart, [id]: (state.cart[id] ?? 0) + quantity } }));
            });
            return true;
        },
        removeFromCart: (id, locale) => get().setCartQuantity(id, 0, locale),
        loadCart: async locale => {
            if (get().loadedLocale === locale) return;
            const requestSessionVersion = useAuthStore.getState().sessionVersion;
            const requestOperationVersion = cartOperationVersion;
            try {
                const data = await cartApi.getCart(locale);
                const completedAuth = useAuthStore.getState();
                if (completedAuth.status !== "authenticated" || completedAuth.sessionVersion !== requestSessionVersion || requestOperationVersion !== cartOperationVersion) return;
                set({ ...cartState(data), loadedLocale: locale, error: null });
            } catch (error) {
                const currentAuth = useAuthStore.getState();
                if (currentAuth.status !== "authenticated" || currentAuth.sessionVersion !== requestSessionVersion) return;
                set({ error: error instanceof Error ? error.message : "Cart loading failed." });
            }
        },
        clearCart: () => set({
            cart: {},
            cartItems: {},
            summary: null,
            pendingProductIds: {},
            pendingSessionVersions: {},
            loadedLocale: null,
            error: null
        })
    };
});
